'use client';
import SellerAdminControls from '@c/admin/SellerAdminControls';
import { FlagIcon, GlobeSimpleIcon } from '@c/icons';
import ProductsPage from '@c/ProductsPage';
import { SellerAccolade, AccoladeTooltip } from '@c/seller/Accolades';
import { ReviewDocument } from '@models/review';
import { useQuery } from '@tanstack/react-query';
import { TabItem } from '@ui/Tabs';
import { getAverageTimeToShip } from '@util/firestore/orders';
import {
  addFollowers,
  getUserByUsernameSlug,
  removeFollower,
} from '@util/firestore/users';
import useListingCount from '@util/hooks/useListingCount';
import { StatusLabel, useOnlineStatus } from '@util/hooks/useOnlineStatus';
import { PageSideFilters, TopFilters } from '@util/hooks/useProductsPage';
import { useTabs } from '@util/hooks/useTabs';
import { formatCurrency } from '@util/index';
import { logError } from '@util/logError';
import { Pagination } from '@util/types/paginator';
import { ArcElement, Chart as ChartJS, Tooltip } from 'chart.js';
import { useAuth } from 'context/AuthContext';
import { useChat } from 'context/ChatContext';
import { useToastContext } from 'context/ToastContext';
import {
  CircleCheckIcon,
  EllipsisVerticalIcon,
  HeartIcon,
  MessageSquareMoreIcon,
  ShareIcon,
  StarIcon,
} from 'lucide-react';
import { injectProfileScript } from 'features/schemaMarkups';
import { CategoryDocument } from 'models/category';
import { FacetCounts, ProductCoupon, ProductDocument } from 'models/product';
import { PublicUserDocument } from 'models/user';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import VerifiedShieldIcon from './icons/VerifiedShieldIcon';
import SellerProfileReviews from './SellerProfileReviews';
import SellerProfileInfo from './SellerProfileInfo';
import ReviewsTab from './ReviewsTab';
import SellerAvatar from '@ui/SellerAvatar';
import ShareModal from '@c/modals/ShareModal';
import ReportItemModal from '@c/modals/ReportItemModal';
import { prefix } from '@util/imagePrefix';
ChartJS.register(ArcElement, Tooltip);

export type SellerProfileProps = {
  user: PublicUserDocument;
  // sellerLocation?: string;
  reviews?: ReviewDocument[];
  auctions?: ProductDocument[];
  initialProducts: ProductDocument[];
  initialPageInfo: Pagination;
  initialTopFilters: TopFilters;
  initialSideFilters: PageSideFilters;
  sellers?: {
    [key: string]: PublicUserDocument;
  };
  categories: CategoryDocument[];
  facet_counts?: FacetCounts;
  allBrands: string[];
  mostly_parts?: boolean;
  queryTab?: string;
  keyword: string | null;
};

const SellerProfileListings = ({
  user,
  initialProducts = [],
  initialPageInfo,
  initialTopFilters,
  initialSideFilters,
  sellers,
  facet_counts,
  categories,
  allBrands,
  mostly_parts,
  queryTab,
  keyword,
}: SellerProfileProps) => {
  return (
    <div className="w-full">
      <ProductsPage
        facet_counts={facet_counts}
        initialProducts={initialProducts}
        initialPageInfo={initialPageInfo}
        initialTopFilters={initialTopFilters}
        initialSideFilters={initialSideFilters}
        pageHeaderTitle={user.username}
        pageType="profile"
        keyword={keyword}
        categories={categories}
        seller_id={user.uid}
        allBrands={allBrands}
        sellers={sellers ?? {}}
        showSearch
        mostly_parts={mostly_parts}
      />
    </div>
  );
};

const SellerProfile = (props: SellerProfileProps) => {
  if (
    !props.user ||
    !props.initialPageInfo ||
    !props.initialTopFilters ||
    !props.initialSideFilters
  ) {
    const el = document.getElementById('__NEXT_DATA__') as HTMLScriptElement;
    logError(
      'ProfilePage props undefined ' + JSON.stringify(props),
      undefined,
      el.innerHTML
    );
  }

  const {
    user,
    reviews,
    auctions,
    initialProducts,
    initialPageInfo,
    initialTopFilters,
    initialSideFilters,
    sellers,
    facet_counts,
    categories,
    allBrands,
    mostly_parts,
    queryTab,
    keyword,
  } = props;

  const router = useRouter();
  const searchParams = useSearchParams();
  const { userDoc, refreshUserDoc } = useAuth();
  const { showSuccessToast } = useToastContext();

  const tabs: TabItem[] = [
    {
      title: 'Listings',
      id: 'listings',
      contents: (
        <SellerProfileListings
          user={user}
          sellers={sellers}
          initialProducts={initialProducts}
          initialPageInfo={initialPageInfo}
          initialTopFilters={initialTopFilters}
          initialSideFilters={initialSideFilters}
          facet_counts={facet_counts}
          categories={categories}
          allBrands={allBrands}
          mostly_parts={mostly_parts}
          queryTab={queryTab}
          keyword={keyword}
        />
      ),
    },
    {
      title: 'Sale',
      id: 'sale',
      contents: (
        <SellerProfileListings
          user={user}
          sellers={sellers}
          initialProducts={initialProducts.filter((product) => product.on_sale)}
          initialPageInfo={{
            ...initialPageInfo,
            totalResults: initialProducts.filter((product) => product.on_sale)
              .length,
            totalPages: Math.ceil(
              initialProducts.filter((product) => product.on_sale).length /
                initialTopFilters.limit
            ),
          }}
          initialTopFilters={initialTopFilters}
          initialSideFilters={initialSideFilters}
          facet_counts={facet_counts}
          categories={categories}
          allBrands={allBrands}
          mostly_parts={mostly_parts}
          queryTab={queryTab}
          keyword={keyword}
        />
      ),
    },
    {
      title: 'Reviews',
      id: 'reviews',
      contents: <ReviewsTab user={user} reviews={reviews ?? []} />,
    },
    {
      title: 'Info & Policies',
      id: 'info',
      contents: <SellerProfileInfo user={user} />,
    },
  ];

  if (
    [
      'Atlasanimalrescue',
      'road2recovery',
      'veteranmx',
      'MXLockerSoCal',
    ].includes(user.username) &&
    auctions
  ) {
    const filteredProducts = auctions.filter((product) => {
      // side filters
      if (!product.date_added) return false;
      if (initialSideFilters.onsale && !product.on_sale) return false;
      if (
        initialSideFilters.minPrice &&
        product.price < initialSideFilters.minPrice
      )
        return false;
      if (
        initialSideFilters.maxPrice &&
        product.price > initialSideFilters.maxPrice
      )
        return false;
      if (initialSideFilters.conditions?.length > 0) {
        if (
          !initialSideFilters.conditions.includes(
            product.condition.toLowerCase() as (typeof initialSideFilters.conditions)[number]
          )
        )
          return false;
      }
      if (initialSideFilters.brands?.length > 0) {
        if (
          !initialSideFilters.brands.includes(
            product.brand as (typeof initialSideFilters.brands)[number]
          )
        )
          return false;
      }
      if (
        initialSideFilters.category &&
        initialSideFilters.category?.length > 0
      ) {
        if (!initialSideFilters.category.includes(product.category as any))
          return false;
      }
      return true;
    });
    // sort by top filters
    const sortedFilteredResults = filteredProducts.sort((a, b) => {
      switch (initialTopFilters.sort) {
        case 'priceAsc':
          return a.price - b.price;
        case 'priceDesc':
          return b.price - a.price;
        case 'newest':
          return b.created - a.created;
        default:
          return 0;
      }
    });
    const page = searchParams?.get('page') ? +searchParams.get('page')! : 1;
    const paginatedSortedFilteredResults = sortedFilteredResults.slice(
      initialTopFilters.limit * (page - 1),
      initialTopFilters.limit * page
    );
    tabs.push({
      title: 'Auctions',
      id: 'auctions',
      contents: (
        <SellerProfileListings
          user={user}
          sellers={sellers}
          initialProducts={paginatedSortedFilteredResults ?? []}
          initialPageInfo={{
            page,
            totalPages: Math.ceil(
              sortedFilteredResults.length / initialTopFilters.limit
            ),
            totalResults: sortedFilteredResults.length,
            itemsPerPage: initialTopFilters.limit,
          }}
          initialTopFilters={initialTopFilters}
          initialSideFilters={initialSideFilters}
          categories={[]}
          allBrands={allBrands}
          keyword={keyword}
        />
      ),
    });
  }
  const [openModal, setOpenModal] = useState<
    'returnPolicy' | 'reportItem' | 'share' | '' | 'options'
  >('');

  const { openChatModalWithRecipientAndProductId } = useChat();
  const { status } = useOnlineStatus(user.uid);
  const [followingData, setFollowingData] = useState<
    boolean | null | undefined
  >(null);

  useEffect(() => {
    const findIndex = userDoc?.following?.findIndex(
      (item) => item === user.uid
    );
    const data = userDoc?.following?.[findIndex as number];
    if (data) {
      setFollowingData(true);
    } else {
      setFollowingData(false);
    }
  }, [userDoc?.uid, userDoc?.following]);

  const handleFollowClick = async () => {
    if (!userDoc) {
      return router.push('/login');
    }

    const followingData = user.uid;

    const findIndex = userDoc?.following.findIndex((item) => item === user.uid);
    const data = userDoc?.following[findIndex as number];

    if (data) {
      removeFollower(userDoc?.uid, followingData);
      refreshUserDoc(userDoc?.uid);
      setFollowingData(false);
      showSuccessToast(`You unfollowed ${user?.username}`);
      return;
    } else {
      addFollowers(userDoc?.uid, followingData);
      refreshUserDoc(userDoc?.uid);
      setFollowingData(true);
      showSuccessToast(`You started following ${user?.username}`);
      return;
    }
  };

  const {
    currentBody,
    tabs: tabButtons,
    setCurrent,
  } = useTabs({
    tabItems: tabs,
    initial:
      queryTab === 'sale'
        ? 1
        : queryTab === 'reviews'
        ? 2
        : queryTab === 'info'
        ? 3
        : queryTab === 'auctions'
        ? 4
        : 0,
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const hash = window.location.hash;
      if (hash === '#auctions') {
        setCurrent(4);
      }
    }
  }, []);
  const { data: avgTimeToShip = user.time_to_ship } = useQuery({
    queryKey: ['avgTimeToShip', user?.uid],
    queryFn: () => getAverageTimeToShip(user!.uid),
    enabled: !!user && !!userDoc?.roles?.support && !user.time_to_ship,
  });

  const { data: sellerDoc } = useQuery({
    queryKey: ['fullUser', user?.uid],
    queryFn: () => getUserByUsernameSlug(user?.username_slug),
    enabled: !!user?.username_slug && !!userDoc?.roles?.support,
  });

  const listingsCount = useListingCount(user?.uid);

  return (
    <>
      <script
        id="profileSchema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: injectProfileScript(user),
        }}
      ></script>
      <div className="z-10 flex w-full flex-col rounded-t-2xl sm:mx-auto sm:max-w-content">
        <div className="relative h-[13rem] w-full sm:h-[35rem]">
          <div
            className="h-full w-full"
            style={{
              backgroundImage: !user?.store_photo
                ? `url('${prefix}/assets/default-profile-banner_1920x2560.png')`
                : `url('${user?.store_photo}')`,
              backgroundSize: 'cover',
            }}
          />

          <div className="absolute right-16 top-10 hidden items-center gap-x-16 sm:flex">
            <button
              type="button"
              className="flex items-center gap-x-2 text-white transition-all duration-150 ease-out hover:opacity-80"
              onClick={() => {
                setOpenModal('share');
              }}
            >
              <ShareIcon className={'h-10 w-10'} />
              <span className="text-[1.8rem] font-semibold text-white">
                Share
              </span>
            </button>

            <button
              className="flex items-center gap-x-2 text-white transition-all duration-150 ease-out hover:opacity-80"
              type="button"
              onClick={() => setOpenModal('reportItem')}
            >
              <FlagIcon className={'h-10 w-10'} />
              <span className="text-[1.8rem] font-semibold text-white">
                Report
              </span>
            </button>
          </div>
        </div>

        <ShareModal
          id={user.uid}
          isOpen={openModal === 'share'}
          title={user.username}
          thumb={user.photo}
          type="user"
          dismiss={() => setOpenModal('')}
        />

        <ReportItemModal
          isUser
          itemId={user.uid}
          isOpen={openModal === 'reportItem'}
          dismiss={() => setOpenModal('')}
        />

        <div className="mt-8 flex w-full flex-col gap-x-32 p-4 pl-8 sm:mt-0 sm:flex-row sm:py-8 sm:pl-16">
          <div className="flex flex-col items-start gap-y-8">
            <div className="flex flex-row items-center gap-8 sm:flex-col sm:gap-6">
              <SellerAvatar user={user} status={status} />

              <div className="flex flex-col items-start gap-y-4">
                <div className="flex items-center gap-x-4">
                  <h3 className="text-[2.4rem] font-bold text-black sm:text-[3rem]">
                    {user.username}
                  </h3>

                  {user.is_verified && (
                    <VerifiedShieldIcon className="h-12 w-12 text-green-500" />
                  )}
                </div>

                <div className="hidden sm:flex">
                  <StatusLabel
                    status={status}
                    hide_status={!!user.hide_status}
                  />
                </div>

                <div className="flex items-center gap-x-2 sm:hidden">
                  <FollowButton
                    following={followingData ?? false}
                    onFollow={handleFollowClick}
                  />

                  <button
                    type="button"
                    className="flex h-[5.5rem] w-[5.5rem] items-center justify-center rounded-[1.5rem] border border-zinc-800 p-4"
                    onClick={() =>
                      openChatModalWithRecipientAndProductId({
                        recipientUid: user.uid,
                      })
                    }
                  >
                    <MessageSquareMoreIcon className="h-full w-full" />
                  </button>

                  <div className="relative">
                    <button
                      type="button"
                      className="flex h-[5.5rem] w-[5.5rem] items-center justify-center rounded-[1.5rem] border border-zinc-800 p-4"
                      onClick={() => setOpenModal(openModal ? '' : 'options')}
                    >
                      <EllipsisVerticalIcon className="h-full w-full" />
                    </button>

                    {openModal === 'options' && (
                      <div className="absolute right-0 top-[6rem] z-10 flex w-[15rem] flex-col rounded-[1rem] border border-zinc-200 bg-white py-2 shadow-lg">
                        <button
                          type="button"
                          className="flex items-center gap-x-3 px-6 py-4 text-[1.8rem] hover:bg-zinc-100"
                          onClick={() => setOpenModal('share')}
                        >
                          <ShareIcon className="h-6 w-6" />
                          Share
                        </button>
                        <button
                          type="button"
                          className="flex items-center gap-x-3 px-6 py-4 text-[1.8rem] hover:bg-zinc-100"
                          onClick={() => setOpenModal('reportItem')}
                        >
                          <FlagIcon className="h-6 w-6" />
                          Report
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex h-[8rem] w-full items-center justify-between gap-x-6 px-10 sm:justify-normal sm:gap-x-32">
              <div className="flex flex-col-reverse items-center gap-y-0 whitespace-nowrap sm:hidden sm:flex-col sm:items-start sm:gap-y-2 sm:border-r-0">
                <h4 className="text-[1.8rem] font-medium text-[#444444]">
                  {user.reviews} Reviews
                </h4>

                <div className="flex items-center gap-x-2">
                  <StarIcon className="h-8 w-8 fill-brand-secondary text-brand-secondary" />

                  <span className="text-[2.5rem] font-bold -tracking-[0.1rem] text-black">
                    {user.rating.toFixed(1)}
                  </span>
                </div>
              </div>

              <div className="block h-[6rem] w-px bg-gray-400 sm:hidden" />

              <div className="flex flex-col-reverse items-center gap-y-0 whitespace-nowrap sm:flex-col sm:items-start sm:gap-y-2 sm:border-r-0">
                <h4 className="text-[1.8rem] font-medium text-[#444444]">
                  Listings
                </h4>

                <span className="text-[2.5rem] font-bold -tracking-[0.1rem] text-black">
                  {listingsCount}
                </span>
              </div>

              <div className="block h-[6rem] w-px bg-gray-400 sm:hidden" />

              <div className="flex flex-col-reverse items-center gap-y-0 whitespace-nowrap sm:flex-col sm:items-start sm:gap-y-2">
                <h4 className="text-[1.8rem] font-medium text-[#444444]">
                  Followers
                </h4>

                <span className="text-[2.5rem] font-bold -tracking-[0.1rem] text-black">
                  {user.followers}
                </span>
              </div>
            </div>

            <div className="hidden items-center gap-x-8 sm:flex">
              <FollowButton
                following={followingData ?? false}
                onFollow={handleFollowClick}
              />

              <button
                type="button"
                className="flex h-[5.5rem] w-[20rem] items-center justify-center gap-x-2 rounded-[1.5rem] border border-zinc-800 bg-transparent text-[1.8rem] font-semibold text-zinc-800"
                onClick={() =>
                  openChatModalWithRecipientAndProductId({
                    recipientUid: user.uid,
                  })
                }
              >
                <MessageSquareMoreIcon className="h-[2.5rem] w-[2.5rem]" />
                Message
              </button>
            </div>

            {!!user.created && (
              <span className="hidden text-[1.8rem] font-medium text-brand-gray sm:block">
                Joined{' '}
                {new Date(user.created ?? 0).toLocaleString('default', {
                  month: 'short',
                  year: 'numeric',
                })}
              </span>
            )}
          </div>

          <div className="hidden w-full flex-col gap-y-8 sm:flex">
            {user.country && (
              <div className="flex items-center gap-x-2">
                <GlobeSimpleIcon className="h-10 w-10 !stroke-zinc-800" />

                <span className="text-[1.8rem] font-medium text-zinc-800">
                  {user.country?.name}
                </span>
              </div>
            )}

            {user.bio ? (
              <p className="w-[80rem] text-[1.8rem] font-medium text-brand-gray">
                {user.bio}
              </p>
            ) : (
              <p className="w-[80rem] text-[1.8rem] font-medium text-zinc-600">
                This seller has no bio.
              </p>
            )}

            <div className="flex items-center justify-center gap-6 px-4 sm:flex-row sm:justify-normal sm:gap-x-16">
              <SellerAccolade
                accolade="quick_responder"
                count={user.badge_counts?.quick_replies ?? 0}
                isProfilePage
              />
              <SellerAccolade
                accolade="fast_shipper"
                count={user.badge_counts?.fast_shipper ?? 0}
                isProfilePage
              />
              <SellerAccolade
                accolade="accurate_describer"
                count={user.badge_counts?.as_described ?? 0}
                isProfilePage
              />
            </div>

            <SellerProfileReviews
              reviews={reviews ?? []}
              user={user}
              onChangeTab={() => setCurrent(2)}
            />
          </div>
        </div>

        <div className="flex w-full items-center justify-center gap-x-8 pb-8">
          {userDoc?.roles?.support && (
            <>
              <div className="flex items-center gap-x-8">
                <div className="flex flex-col items-center">
                  <div className="flex items-center gap-[0.8rem] text-[2.4rem] font-semibold">
                    {formatCurrency(sellerDoc?.metrics.ltv_purchased ?? 0)}
                  </div>
                  <div>Purchase LTV</div>
                </div>
                <div className="flex flex-col items-center">
                  <div className="flex items-center gap-[0.8rem] text-[2.4rem] font-semibold">
                    {formatCurrency(sellerDoc?.metrics.ltv_sold ?? 0)}
                  </div>
                  <div>Sold LTV</div>
                </div>
              </div>

              <div className="flex flex-col items-center">
                <div className="flex items-center gap-[0.8rem] text-[2.4rem] font-semibold">
                  {(
                    ((user?.num_refunded ?? 0) / (user?.num_sold ?? 1)) *
                    100
                  ).toFixed(2)}{' '}
                  %
                </div>
                <div>Refunds</div>
              </div>
              <div className="flex flex-col items-center">
                <div className="flex items-center gap-[0.8rem] text-[2.4rem] font-semibold">
                  {avgTimeToShip ?? 0}
                </div>
                <div>Average Days to Ship</div>
              </div>
            </>
          )}
        </div>

        {!!user.store_wide_sale && (
          <p className="marquee bg-brand-secondary py-4 text-center font-semibold text-white sm:text-[2.4rem]">
            <span>STORE WIDE SALE {user.store_wide_sale}%+ OFF EVERYTHING</span>
          </p>
        )}

        <div className="block sm:hidden">
          <div className="flex flex-wrap items-center justify-center gap-6 px-4 sm:flex-row sm:justify-normal sm:gap-x-16">
            <SellerAccolade
              accolade="quick_responder"
              count={user.badge_counts?.quick_replies ?? 0}
              isProfilePage
            />
            <SellerAccolade
              accolade="fast_shipper"
              count={user.badge_counts?.fast_shipper ?? 0}
              isProfilePage
            />
            <SellerAccolade
              accolade="accurate_describer"
              count={user.badge_counts?.as_described ?? 0}
              isProfilePage
            />
          </div>
        </div>

        <div className="mt-8 flex items-center gap-x-8 p-8 sm:hidden">
          <span className="text-[2rem] font-medium text-brand-dark-gray">
            Joined{' '}
            {new Date(user.created ?? 0).toLocaleString('default', {
              month: 'short',
              year: 'numeric',
            })}
          </span>

          {user.country && (
            <div className="flex items-center gap-x-2">
              <GlobeSimpleIcon className="h-10 w-10 !stroke-brand-dark-gray" />

              <span className="text-dark-graystroke-brand-dark-gray text-[2rem] font-medium">
                {user.country?.name}
              </span>
            </div>
          )}
        </div>

        {!!userDoc?.roles?.support && <SellerAdminControls doc={user} />}
        <div className="w-full bg-white">
          <div className="w-full px-6 sm:px-0">{tabButtons}</div>
        </div>

        <div className="w-full">{currentBody}</div>
      </div>
    </>
  );
};

const FollowButton = ({
  following,
  onFollow,
}: {
  following: boolean;
  onFollow: () => Promise<void>;
}) => {
  if (following) {
    return (
      <button
        type="button"
        className="flex h-[5.5rem] w-[20rem] items-center justify-center gap-x-2 rounded-[1.5rem] bg-[#E9F2E9] text-[1.8rem] font-semibold text-[#1E7A1E] transition-colors duration-150 ease-linear hover:bg-[#D9E8D9]"
        onClick={onFollow}
      >
        <CircleCheckIcon className="h-[2.5rem] w-[2.5rem] fill-[#1E7A1E] text-white" />
        Following
      </button>
    );
  } else {
    return (
      <button
        type="button"
        className="flex h-[5.5rem] w-[20rem] items-center justify-center gap-x-2 rounded-[1.5rem] bg-brand-secondary text-[1.8rem] font-semibold text-white transition-colors duration-150 ease-linear hover:bg-brand-secondary-darker"
        onClick={onFollow}
      >
        <HeartIcon className="h-8 w-8 text-white" /> Follow
      </button>
    );
  }
};

export default SellerProfile;
